import React, { FormEvent, useEffect, useState } from 'react';
import api from '../../api/api';

type AccountingPeriod = {
  id: number;
  startDate: string;
  endDate: string;
  status: 'Open' | 'Closed';
  closedByUsername: string;
  closedAtUtc: string | null;
  version: number;
};

type GroupSelection = {
  selectedGroupId: number | null;
  groups: Array<{ id: number; name: string }>;
  allGroupsOverview?: boolean;
  canViewAllGroups?: boolean;
};

type LegacyMappingPreview = {
  targetOperatingGroupId: number;
  targetName: string;
  fingerprint: string;
  canApply: boolean;
  candidates: AccountingPeriod[];
  overlaps: Array<{
    legacyPeriodId: number;
    targetPeriodId: number;
    legacyStartDate: string;
    legacyEndDate: string;
    targetStartDate: string;
    targetEndDate: string;
  }>;
};

const today = () => {
  const date = new Date();
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 10);
};

const errorMessage = (error: unknown, fallback: string) => {
  const response = (error as { response?: { data?: { message?: string } } })?.response;
  return response?.data?.message || fallback;
};

const dateLabel = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('uk-UA');
};

const AccountingPeriodsPage: React.FC = () => {
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [groupName, setGroupName] = useState('');
  const [canViewAllGroups, setCanViewAllGroups] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mappingPreview, setMappingPreview] = useState<LegacyMappingPreview | null>(null);
  const [mappingConfirmation, setMappingConfirmation] = useState('');
  const [mappingBusy, setMappingBusy] = useState(false);

  const loadPeriods = async () => {
    const response = await api.get<AccountingPeriod[]>('/accounting-periods');
    setPeriods(response.data);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get<GroupSelection>('/operating-contexts/selection');
        if (!active) return;
        const group = data.groups.find(item => item.id === data.selectedGroupId);
        if (!group || data.allGroupsOverview) {
          setGroupName('');
          setCanViewAllGroups(data.canViewAllGroups === true);
          setError('Оберіть конкретну операційну групу у верхній панелі. Облікові періоди не працюють в огляді всіх груп.');
          return;
        }
        setGroupName(group.name);
        setCanViewAllGroups(data.canViewAllGroups === true);
        await loadPeriods();
      } catch (loadError) {
        if (active) setError(errorMessage(loadError, 'Не вдалося завантажити облікові періоди.'));
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, []);

  const createPeriod = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/accounting-periods', { startDate, endDate });
      await loadPeriods();
    } catch (createError) {
      setError(errorMessage(createError, 'Не вдалося створити обліковий період.'));
    } finally {
      setSaving(false);
    }
  };

  const closePeriod = async (period: AccountingPeriod) => {
    if (!window.confirm(`Закрити обліковий період ${dateLabel(period.startDate)} – ${dateLabel(period.endDate)}?`)) return;
    setError('');
    try {
      await api.post(`/accounting-periods/${period.id}/close`);
      await loadPeriods();
    } catch (closeError) {
      setError(errorMessage(closeError, 'Не вдалося закрити обліковий період.'));
    }
  };

  const previewLegacyMapping = async () => {
    setError('');
    setMappingPreview(null);
    try {
      const response = await api.get<LegacyMappingPreview>('/accounting-periods/legacy-halls-mapping/preview');
      setMappingPreview(response.data);
      setMappingConfirmation('');
    } catch (previewError) {
      setError(errorMessage(previewError, 'Не вдалося підготувати попередній перегляд перенесення.'));
    }
  };

  const applyLegacyMapping = async () => {
    if (!mappingPreview) return;
    setError('');
    setMappingBusy(true);
    try {
      await api.post('/accounting-periods/legacy-halls-mapping/apply', {
        periodIds: mappingPreview.candidates.map(period => period.id),
        fingerprint: mappingPreview.fingerprint,
        confirmation: mappingConfirmation,
      });
      setMappingPreview(null);
      setMappingConfirmation('');
      await loadPeriods();
    } catch (mappingError) {
      setError(errorMessage(mappingError, 'Не вдалося прив’язати legacy-періоди до HALLS.'));
      setMappingPreview(null);
    } finally {
      setMappingBusy(false);
    }
  };

  return (
    <main className="container" style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      <h1>Облікові періоди</h1>
      <p>Операційна група: <strong>{groupName || 'не вибрана'}</strong></p>
      {error && <div role="alert" className="alert alert-danger">{error}</div>}

      <form onSubmit={createPeriod} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: 12, margin: '20px 0' }}>
        <label>
          Початок періоду
          <input required type="date" value={startDate} onChange={event => setStartDate(event.target.value)}
            style={{ display: 'block', minHeight: 44, padding: 8, fontSize: 16 }} />
        </label>
        <label>
          Кінець періоду
          <input required type="date" value={endDate} onChange={event => setEndDate(event.target.value)}
            style={{ display: 'block', minHeight: 44, padding: 8, fontSize: 16 }} />
        </label>
        <button type="submit" disabled={saving || loading || !groupName} className="btn btn-primary">
          {saving ? 'Збереження…' : 'Створити період'}
        </button>
      </form>

      {loading ? <p>Завантаження…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th scope="col">Початок</th>
                <th scope="col">Кінець</th>
                <th scope="col">Стан</th>
                <th scope="col">Закрито</th>
                <th scope="col">Дія</th>
              </tr>
            </thead>
            <tbody>
              {periods.map(period => (
                <tr key={period.id}>
                  <td>{dateLabel(period.startDate)}</td>
                  <td>{dateLabel(period.endDate)}</td>
                  <td>{period.status === 'Closed' ? 'Закрито' : 'Відкрито'}</td>
                  <td>{period.closedAtUtc
                    ? `${dateLabel(period.closedAtUtc)}${period.closedByUsername ? `, ${period.closedByUsername}` : ''}`
                    : '—'}</td>
                  <td>
                    {period.status === 'Closed' ? '—' : (
                      <button type="button" className="btn btn-outline-danger" onClick={() => void closePeriod(period)}>
                        Закрити
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {periods.length === 0 && <tr><td colSpan={5}>Для цієї групи ще немає облікових періодів.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {groupName.toLocaleUpperCase('uk-UA') === 'HALLS' && canViewAllGroups && (
        <section aria-labelledby="legacy-mapping-heading" style={{ marginTop: 32 }}>
          <h2 id="legacy-mapping-heading">Перенесення legacy-періодів HALLS</h2>
          <p>Перегляньте та підтвердьте прив’язку періодів без власника. Дані не змінюються без точного підтвердження.</p>
          <button type="button" className="btn btn-outline-secondary" onClick={() => void previewLegacyMapping()}>
            Попередній перегляд
          </button>
          {mappingPreview && (
            <div style={{ marginTop: 16 }}>
              <p>
                Періодів для перенесення: {mappingPreview.candidates.length}.
                {mappingPreview.canApply ? ' Перетинів немає.' : ` Виявлено перетинів: ${mappingPreview.overlaps.length}.`}
              </p>
              {mappingPreview.candidates.map(period => (
                <div key={period.id}>{dateLabel(period.startDate)} – {dateLabel(period.endDate)} ({period.status})</div>
              ))}
              {mappingPreview.overlaps.map(overlap => (
                <p key={`${overlap.legacyPeriodId}-${overlap.targetPeriodId}`} role="alert">
                  Legacy #{overlap.legacyPeriodId} перетинається з HALLS #{overlap.targetPeriodId}:
                  {' '}{dateLabel(overlap.legacyStartDate)} – {dateLabel(overlap.legacyEndDate)} /
                  {' '}{dateLabel(overlap.targetStartDate)} – {dateLabel(overlap.targetEndDate)}.
                </p>
              ))}
              <label style={{ display: 'block', marginTop: 12 }}>
                Для підтвердження введіть <code>MAP LEGACY ACCOUNTING PERIODS TO HALLS</code>
                <input value={mappingConfirmation} onChange={event => setMappingConfirmation(event.target.value)}
                  autoComplete="off" style={{ display: 'block', width: 'min(100%, 520px)', minHeight: 44, padding: 8, fontSize: 16 }} />
              </label>
              <button type="button" className="btn btn-danger" disabled={!mappingPreview.canApply ||
                mappingConfirmation !== 'MAP LEGACY ACCOUNTING PERIODS TO HALLS' || mappingBusy}
                onClick={() => void applyLegacyMapping()}>
                {mappingBusy ? 'Перенесення…' : 'Підтвердити прив’язку до HALLS'}
              </button>
            </div>
          )}
        </section>
      )}
    </main>
  );
};

export default AccountingPeriodsPage;
